// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { SearchTypes } from "@mcmc/App/Store/Selectors/SearchResultsSelectors";
import { Roboto, RobotoCondensed, styledScrollbar } from "@mcmc/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface LinkListProps {
    accordionSection?: AccordionSectionPresentationProps;
    link?: LinkPresentationProps;
    contentWrapper?: InjectableCss;
}

export const linkListStyle: LinkListProps = {
    accordionSection: {
        titleTypographyProps: {
            ...RobotoCondensed,
            weight: "bold",
            size: "16px",
            lineHeight: "19px",
            css: css`
                text-transform: uppercase;
            `,
        },
        headerProps: {
            css: css`
                ${getProp("theme.accordion.sectionDefaultProps.headerProps.css")}
                && {
                    button,
                    button:focus {
                        padding: 13px 20px;
                    }
                }
            `,
            "aria-level": 2,
        },
        panelProps: {
            css: css`
                ${getProp("theme.accordion.sectionDefaultProps.panelProps.css")}
                padding: 10px 10px 10px 15px;
            `,
        },
    },
    link: {
        color: "text.main",
        hoverStyle: { textDecoration: "underline" },
        typographyProps: {
            ...Roboto,
            weight: 400,
        },
        css: css`
            display: inline-block;
            letter-spacing: 0;
            margin: 0 5px 8px;
        `,
    },
    contentWrapper: {
        css: css`
            overflow-y: auto;
            max-height: 338px;
            padding: 5px 0;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            ${styledScrollbar()};
        `,
    },
};

const styles = linkListStyle;

interface OwnProps {
    categoryId?: string;
    includeProductsInSubCategories?: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const productsDataView = getProductsDataView(state, {
        expand: ["facets"],
        categoryId: ownProps.categoryId,
        includeProductsInSubCategories: ownProps.includeProductsInSubCategories,
        populateBrandListPagePath: true,
        pageSize: 1,
    } as any);
    const productCollection = productsDataView.value ? productsDataView : undefined;
    return {
        brandFacets: productCollection?.brandFacets,
    };
};

const mapDispatchToProps = {
    loadProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const BrandsList: FC<Props> = ({ categoryId, includeProductsInSubCategories, loadProducts, brandFacets }) => {
    useEffect(() => {
        if (!brandFacets) {
            loadProducts({
                expand: ["facets"],
                categoryId,
                includeProductsInSubCategories,
                populateBrandListPagePath: true,
                pageSize: 1,
            } as any);
        }
    }, []);

    if (!brandFacets) {
        return null;
    }

    return (
        <AccordionSection title={translate("Brands")} {...styles.accordionSection}>
            <StyledWrapper {...styles.contentWrapper}>
                {brandFacets.map((brand: any) => (
                    <Link key={brand.id} {...styles.link} href={brand.productListPagePath}>
                        {brand.name}
                    </Link>
                ))}
            </StyledWrapper>
        </AccordionSection>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(BrandsList);
